import { Icon } from '@natera/material/lib/icon'
import { Select } from 'radix-ui'
import { useRef, useState } from 'react'
import { cn } from '~/shared/lib/cn'
import { Text } from '~/shared/ui/text'

interface SelectProps {
  label?: React.ReactNode
  value?: string | number
  defaultValue?: string | number
  onValueChange?: (value: string | number) => void
  placeholder?: string
  name?: string
  required?: boolean
  disabled?: boolean
  error?: string
  hint?: string
  help?: boolean
  className?: string
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  onBlur?: React.FocusEventHandler<HTMLButtonElement>
  children: React.ReactNode
}

export function SelectRoot({
  label,
  value,
  defaultValue,
  onValueChange,
  placeholder,
  name,
  required = false,
  disabled = false,
  error,
  hint,
  help = false,
  className,
  open,
  defaultOpen,
  onOpenChange,
  onBlur,
  children,
}: SelectProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isControlled = value !== undefined
  const isNumeric = typeof value === 'number' || typeof defaultValue === 'number'
  const stringValue = value !== undefined ? String(value) : undefined
  const stringDefaultValue = defaultValue !== undefined ? String(defaultValue) : undefined

  const [internalValue, setInternalValue] = useState(stringDefaultValue ?? '')
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false)

  const currentValue = isControlled ? stringValue : internalValue
  const hasValue = currentValue !== undefined && currentValue !== ''
  const isLabelFloating = (open ?? isOpen) || hasValue

  const handleValueChange = (newValue: string) => {
    if (!isControlled) setInternalValue(newValue)
    onValueChange?.(isNumeric ? Number(newValue) : newValue)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (open === undefined) setIsOpen(nextOpen)
    if (!nextOpen) {
      // Bridge the gap between data-state removal and focus returning to trigger
      triggerRef.current?.setAttribute('data-closing', '')
      requestAnimationFrame(() => {
        triggerRef.current?.removeAttribute('data-closing')
      })
    }
    onOpenChange?.(nextOpen)
  }

  return (
    <Select.Root
      value={isControlled ? stringValue : undefined}
      defaultValue={isControlled ? undefined : stringDefaultValue}
      onValueChange={handleValueChange}
      open={open}
      defaultOpen={open !== undefined ? undefined : defaultOpen}
      onOpenChange={handleOpenChange}
      disabled={disabled}
      name={name}
    >
      <div
        className={cn('relative w-full', { 'mt-2': label, 'mb-5': hint || error }, className)}
        data-track-name={name}
      >
        <Select.Trigger
          asChild
          aria-invalid={!!error}
        >
          <button
            ref={triggerRef}
            onBlur={onBlur}
            className={cn(
              'group relative flex h-11 w-full cursor-pointer items-center rounded-sm border-none bg-inherit text-left outline-none',
              help ? 'pl-13' : 'pl-4',
              { 'pointer-events-none': disabled },
            )}
          >
            {/* Help icon */}
            {help && (
              <Icon
                className="text-alert-warning-message absolute top-1/2 left-4 -translate-y-1/2"
                children="help"
                fill
              />
            )}

            {/* Border */}
            {label ? (
              <>
                {/* Notch border */}
                <div
                  className={cn(
                    'pointer-events-none absolute inset-0 flex [&_div]:h-full',
                    '[&_div]:border-input-outlined-enabled-border',
                    '[&_div]:transition-[border-color,border-width] [&_div]:duration-150',
                    {
                      'group-hover:not-group-focus-within:[&_div]:border-input-outlined-hovered-border group-focus-within:[&_div]:border-input-outlined-focused-border group-data-[state=open]:[&_div]:border-input-outlined-focused-border group-data-closing:[&_div]:border-input-outlined-focused-border':
                        !error && !disabled,
                      '[&_div]:border-input-outlined-disabled-border': disabled,
                      '[&_div]:border-error-main': error,
                    },
                  )}
                >
                  <div
                    className={cn(
                      'w-3 rounded-l-sm border-t border-b border-l',
                      'group-focus-within:border-t-2 group-focus-within:border-b-2 group-focus-within:border-l-2',
                      'group-data-[state=open]:border-t-2 group-data-[state=open]:border-b-2 group-data-[state=open]:border-l-2',
                      'group-data-closing:border-t-2 group-data-closing:border-b-2 group-data-closing:border-l-2',
                    )}
                  />
                  <div
                    className={cn(
                      'flex items-center border-b',
                      isLabelFloating ? 'border-t-transparent' : 'border-t',
                      'group-focus-within:border-b-2',
                      isLabelFloating
                        ? 'group-focus-within:border-t-transparent'
                        : 'group-focus-within:border-t-2',
                      'group-data-[state=open]:border-b-2',
                      isLabelFloating
                        ? 'group-data-[state=open]:border-t-transparent'
                        : 'group-data-[state=open]:border-t-2',
                      'group-data-closing:border-b-2',
                      isLabelFloating
                        ? 'group-data-closing:border-t-transparent'
                        : 'group-data-closing:border-t-2',
                    )}
                  >
                    <span className="invisible px-1 text-xs leading-4 whitespace-nowrap">
                      {label}
                      {required && '*'}
                    </span>
                  </div>
                  <div className="grow rounded-r-sm border-t border-r border-b group-focus-within:border-t-2 group-focus-within:border-r-2 group-focus-within:border-b-2 group-data-closing:border-t-2 group-data-closing:border-r-2 group-data-closing:border-b-2 group-data-[state=open]:border-t-2 group-data-[state=open]:border-r-2 group-data-[state=open]:border-b-2" />
                </div>

                {/* Floating label */}
                <label
                  className={cn(
                    'bg-surface-lowest pointer-events-none absolute left-3 px-1 transition-all duration-150 ease-in-out',
                    isLabelFloating
                      ? 'top-0 -translate-y-1/2 text-xs leading-4 tracking-[0.4px]'
                      : 'top-1/2 -translate-y-1/2 text-[15px] leading-5 tracking-[0.25px]',
                    'text-text-secondary',
                    'group-focus-within:text-primary-main',
                    {
                      'text-primary-main': (open ?? isOpen) && !error,
                      'text-error-main group-focus-within:text-error-main': error,
                      'text-text-disabled group-focus-within:text-text-disabled': disabled,
                    },
                  )}
                >
                  {label}
                  {required && <span className="text-error-main">*</span>}
                </label>
              </>
            ) : (
              /* Simple border (no label) */
              <div
                className={cn(
                  'pointer-events-none absolute inset-0 rounded-sm border transition-[border-color,border-width] duration-150',
                  'border-input-outlined-enabled-border',
                  {
                    'group-hover:not-group-focus-within:border-input-outlined-hovered-border group-focus-within:border-input-outlined-focused-border group-data-[state=open]:border-input-outlined-focused-border group-data-closing:border-input-outlined-focused-border group-focus-within:border-2 group-data-closing:border-2 group-data-[state=open]:border-2':
                      !error && !disabled,
                    'border-input-outlined-disabled-border': disabled,
                    'border-error-main': error,
                  },
                )}
              />
            )}

            {/* Value display */}
            <span
              className={cn('text-text-primary truncate text-[15px] leading-5 tracking-[0.25px]', {
                'text-text-disabled': disabled,
              })}
            >
              <Select.Value placeholder={placeholder} />
            </span>

            {/* Arrow */}
            <Select.Icon asChild>
              <Icon
                className={cn(
                  'text-text-secondary mx-1.5 ml-auto shrink-0 transition-transform duration-150',
                  { 'rotate-180': open ?? isOpen, 'text-text-disabled': disabled },
                )}
                children="arrow_drop_down"
              />
            </Select.Icon>
          </button>
        </Select.Trigger>

        {/* Hint / Error text */}
        {(error || hint) && (
          <Text
            className={cn(
              'absolute bottom-0 left-0 translate-y-full px-4 py-0.5',
              error ? 'text-error-main' : 'text-text-secondary',
              { 'text-text-disabled': disabled && !error },
            )}
            appearance="CAPTION"
          >
            {error || hint}
          </Text>
        )}
      </div>

      <Select.Portal>
        <Select.Content
          position="popper"
          className={cn(
            'bg-surface-lowest z-50 w-(--radix-select-trigger-width) origin-(--radix-select-content-transform-origin) rounded-sm py-1 outline-hidden',
            'shadow-[0px_1px_6px_0px_rgba(0,0,0,0.04),0px_5.5px_16px_0px_rgba(0,0,0,0.19)]',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
          )}
        >
          <Select.Viewport>{children}</Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}
